import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { UnscheduleHookMaterialFields } from '../hooks/useUnscheduleMaterial';

interface State {
  show: boolean;
  loading: boolean;
  onAction: (unscheduleRelatedMaterial?: boolean) => void;
  material?: UnscheduleHookMaterialFields;
}

const defaultState: State = {
  show: false,
  loading: false,
  onAction: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
};

export const UnscheduleRelatedMaterialContext = createContext<{
  state: State;
  open: (arg: Omit<State, 'show'>) => void;
  close: () => void;
}>({
  state: defaultState,
  open: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  close: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
});

export const useUnscheduleRelatedMaterial = (): ((
  loading: boolean,
  action: (unscheduleRelatedMaterial?: boolean) => void,
  material?: UnscheduleHookMaterialFields,
) => void) => {
  const { open, close } = useContext(UnscheduleRelatedMaterialContext);

  return useCallback(
    (loading, action, material) =>
      open({
        material,
        loading,
        onAction: unscheduleRelatedMaterial => {
          action(unscheduleRelatedMaterial);
          close();
        },
      }),
    [open, close],
  );
};

export const UnscheduleRelatedMaterialContextProvider = ({ children }: { children: ReactNode }) => {
  const [value, setValue] = useState(defaultState);

  return (
    <UnscheduleRelatedMaterialContext.Provider
      value={useMemo(
        () => ({
          state: value,
          open: arg => setValue({ ...arg, show: true }),
          close: () => setValue(defaultState),
        }),
        [value],
      )}
    >
      {children}
    </UnscheduleRelatedMaterialContext.Provider>
  );
};
